import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

import { translate } from '@jsverse/transloco';
import { Subject, debounceTime, takeUntil, tap } from 'rxjs';
import { EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';
import {
  createDashboardLevelWidgetGroup,
  deleteWidgetGroup,
  getDashboardLevelWidgetGroup,
} from 'app/modules/dashboard/stores/widget-group.store';
import { Alias, AliasResourceType, AliasService } from 'app/providers/bridge-api/alias.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import {
  Dashboard,
  DashboardService,
  WidgetGroup,
} from '../../../../../providers/bridge-api/dashboard.service';
import { generalAsyncNameValidator } from '../../../../../utils/customValidators';

@EnableClassTelemetry()
@Component({
  selector: 'app-dashboard-option-general',
  templateUrl: './dashboard-option-general.component.html',
  styleUrls: ['./dashboard-option-general.component.scss'],
  standalone: false,
})
export class DashboardOptionGeneralComponent implements OnInit, OnChanges {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() dashboardOriginal: Dashboard;
  @Input() dashboard: Dashboard;
  @Input() isValid: boolean;
  @Input() isReadOnly: boolean;

  @Output() dashboardChanged = new EventEmitter<Dashboard>();
  @Output() clearUserCache = new EventEmitter<void>();
  @Output() isValidChange = new EventEmitter<boolean>();

  dashboardTitleStatus: ErrorObject = { state: EnumErrorStates.SUCCESS, text: '' };

  isLoading = {
    isValidating: false,
    isAliasDeleting: false,
  };
  nameValidator$ = new Subject();
  unsub$ = new Subject<void>();

  originalWidgetGroup: WidgetGroup;

  aliases: Alias[];
  aliasCreateTooltip;
  aliasDeleteTooltip;
  showCreateAliasModal;

  private dashboardService = inject(DashboardService);
  private aliasService = inject(AliasService);
  private systemService = inject(SystemService);
  private alertService = inject(AlertService);
  private dialogService = inject(DialogService);

  constructor() {}

  public ngOnInit(): void {
    if (this.isReadOnly) {
      this.aliasCreateTooltip = translate(
        'dashboard-options-component.dashboard-option-general.no-permission-create'
      );
      this.aliasDeleteTooltip = translate(
        'dashboard-options-component.dashboard-option-general.no-permission-delete'
      );
    } else {
      this.aliasDeleteTooltip = translate(
        'dashboard-options-component.dashboard-option-general.delete-alias'
      );
    }

    this.aliasService
      .getAliasForResource(AliasResourceType.DASHBOARD, this.dashboard.id)
      .then((res) => (this.aliases = res.result.hits));

    this.nameValidator$
      .pipe(
        takeUntil(this.unsub$),
        tap(() => {
          this.isLoading.isValidating = true;
          this.isValidChange.emit(false);
        }),
        debounceTime(500)
      )
      .subscribe(() => {
        generalAsyncNameValidator(
          this.dashboard.title,
          this.dashboardTitleStatus,
          translate('dashboard-options-component.dashboard-option-general.dashboard'),
          64,
          true,
          () =>
            this.dashboardService.validateDashboardName(
              this.dashboard.title,
              this.dashboard.project_id
            ),
          this.dashboardOriginal.title
        )
          .then((res) => {
            this.dashboardTitleStatus = { ...res };
            this.dashboardChanged.emit(this.dashboard);

            this.isValid = !this.dashboardTitleStatus.state;
          })
          .finally(() => {
            this.isLoading.isValidating = false;
            this.isValidChange.emit(this.isValid);
          });
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.dashboard?.currentValue) {
      this.originalWidgetGroup = getDashboardLevelWidgetGroup(this.dashboard);
    }
  }

  public onSentimentScaleChanged(): void {
    this.dashboard.payload.sentimentScale = this.dashboard.payload.sentimentScale === 3 ? 5 : 3;
    this.dashboardChanged.emit(this.dashboard);
  }

  public onSentimentDisplayChanged(detail: string): void {
    this.dashboard.payload.sentimentDisplay = detail.toLowerCase();
    this.dashboardChanged.emit(this.dashboard);
  }

  onDashboardLevelWidgetGroupChanged() {
    if (this.hasDashboardLevelWidgetGroup(this.dashboard)) {
      deleteWidgetGroup(this.dashboard, this.originalWidgetGroup);
      this.dashboardChanged.emit(this.dashboard);
      return;
    }

    if (!this.originalWidgetGroup) {
      this.originalWidgetGroup = createDashboardLevelWidgetGroup(
        translate(
          'dashboard-options-component.dashboard-option-general.dashboard-filters-widget-group-name'
        )
      );
    }
    this.dashboard.widgetGroups[this.originalWidgetGroup.id] = this.originalWidgetGroup;
    this.dashboardChanged.emit(this.dashboard);
  }

  onCreateAlias() {
    this.showCreateAliasModal = true;
  }

  onCreateAliasModalClosed(result?) {
    this.showCreateAliasModal = false;

    if (result) {
      this.aliases.push(result);
      this.aliases.sort((a1, a2) =>
        a1.name.localeCompare(a2.name, undefined, { sensitivity: 'accent' })
      );
    }
  }

  public onShareableToggle(shareable: boolean, alias: Alias) {
    this.aliasService
      .updateAlias(
        alias.id,
        alias.resourceType,
        alias.resourceId,
        alias.name,
        alias.groupId,
        shareable
      )
      .catch((error) =>
        this.alertService.showErrorToast(
          translate('dashboard-options-component.dashboard-option-general.update-failed'),
          error
        )
      );
  }

  onCopyAlias(alias: Alias) {
    this.systemService.copyToClipboard(`${location.protocol}//${location.host}/d/${alias.name}`);
    this.alertService.showToast(
      translate('dashboard-options-component.dashboard-option-general.alias-copied')
    );
  }

  onDeleteAlias(alias: Alias) {
    this.dialogService
      .showSimpleDialog({
        title: translate('dashboard-options-component.dashboard-option-general.delete-title'),
        content: translate('dashboard-options-component.dashboard-option-general.delete-content'),
        confirmTitle: translate(
          'dashboard-options-component.dashboard-option-general.delete-confirm'
        ),
        cancelTitle: translate('dashboard-options-component.cancel'),
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.alertService.showToast(
            translate('dashboard-options-component.dashboard-option-general.deleting-alias', {
              name: alias.name,
            })
          );
          this.isLoading.isAliasDeleting = true;
          this.aliasService
            .deleteAlias(alias.id)
            .then(() => {
              this.aliases = this.aliases.filter((a) => a !== alias);
              this.alertService.showToast(
                translate('dashboard-options-component.dashboard-option-general.delete-success')
              );
            })
            .catch((error) =>
              this.alertService.showErrorToast(
                translate('dashboard-options-component.dashboard-option-general.delete-failed'),
                error
              )
            )
            .finally(() => (this.isLoading.isAliasDeleting = false));
        }
      });
  }

  hasDashboardLevelWidgetGroup(dashboard: Dashboard) {
    return !!getDashboardLevelWidgetGroup(dashboard);
  }
}
